import React, { useState, useEffect } from 'react';
import { Car, Calendar, Clock, Users, MapPin, MessageCircle, Check, ArrowRight, Luggage, Shield, X, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const QuickBooking = () => {
  const [step, setStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();
  
  const [bookingData, setBookingData] = useState({
    tripType: 'one-way',
    vehicleType: '',
    pickupLocation: '',
    dropLocation: '',
    date: '',
    time: '',
    passengers: 1,
    luggage: 0,
    name: '',
    phone: '',
    email: '',
    specialRequests: ''
  });

  const vehicleTypes = [
    { 
      id: 'sedan', 
      name: 'Sedan', 
      icon: <Car size={16} />, 
      price: '₹14/km',
      features: ['AC', '4 Seater', 'City'],
      capacity: '4 Seats'
    },
    { 
      id: 'suv', 
      name: 'SUV', 
      icon: <Car size={16} />, 
      price: '₹18/km',
      features: ['Spacious', '6-7 Seater', 'AC'],
      capacity: '6-7 Seats'
    },
    { 
      id: 'innova', 
      name: 'Innova', 
      icon: <Car size={16} />, 
      price: '₹22/km',
      features: ['Premium', '6-7 Seater', 'Comfort'],
      capacity: '6-7 Seats'
    },
    { 
      id: 'tempo', 
      name: 'Tempo', 
      icon: <Users size={16} />, 
      price: '₹30/km',
      features: ['Group Travel', '9-26 Seater', 'AC'],
      capacity: '9-26 Seats'
    },
    { 
      id: 'urbania', 
      name: 'Urbania', 
      icon: <Car size={16} />, 
      price: '₹45/km',
      features: ['Luxury', '13-17 Seater', 'Premium'],
      capacity: '13-17 Seats'
    },
    { 
      id: 'luxury', 
      name: 'Luxury', 
      icon: <Car size={16} />, 
      price: '₹50/km',
      features: ['VIP', 'Chauffeur', 'Events'],
      capacity: 'VIP'
    }
  ];

  const whatsappNumber = '919100390049';
  const companyName = 'Shree Puja Travels';

  useEffect(() => {
    // Remove any default body padding/margin and set full height
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflowX = 'hidden';
    
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.style.minHeight = '100vh';
    }

    // Cleanup function
    return () => {
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.body.style.overflowX = '';
      if (rootElement) {
        rootElement.style.minHeight = '';
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBookingData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleVehicleSelect = (vehicleId) => {
    setBookingData(prev => ({
      ...prev,
      vehicleType: vehicleId
    }));
  };

  const nextStep = () => {
    if (step === 1 && (!bookingData.pickupLocation || !bookingData.dropLocation)) {
      alert('Please enter pickup and drop locations');
      return;
    }
    if (step === 2 && !bookingData.vehicleType) {
      alert('Please select a vehicle type');
      return;
    }
    setStep(prev => prev + 1);
  };

  const prevStep = () => {
    setStep(prev => prev - 1);
  };

  const sendWhatsAppMessage = () => {
    if (!bookingData.name || !bookingData.phone) {
      alert('Please enter your name and phone number');
      return;
    }

    const selectedVehicle = vehicleTypes.find(v => v.id === bookingData.vehicleType);

    const message = `🚕 *NEW CAB BOOKING - Shree Puja Travels* 🚕

*Customer Details:*
👤 Name: ${bookingData.name}
📱 Phone: ${bookingData.phone}
${bookingData.email ? `📧 Email: ${bookingData.email}` : ''}

*Trip Details:*
📍 Pickup: ${bookingData.pickupLocation}
🎯 Drop: ${bookingData.dropLocation}
📅 Date: ${bookingData.date}
⏰ Time: ${bookingData.time}
🔄 Trip: ${bookingData.tripType === 'one-way' ? 'One Way' : 'Round Trip'}

*Vehicle Details:*
🚗 Vehicle: ${selectedVehicle?.name}
👥 Passengers: ${bookingData.passengers}
🧳 Luggage: ${bookingData.luggage} bags
💰 Rate: ${selectedVehicle?.price}

*Special Requests:*
${bookingData.specialRequests || 'None'}

---
*Booking Time:* ${new Date().toLocaleString('en-IN')}
*Booking ID:* SPT${Date.now().toString().slice(-8)}

Please confirm availability.`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    
    // Open WhatsApp
    window.open(whatsappUrl, '_blank');
    
    // Show success popup
    setShowSuccess(true);
  };

  const handleReturnHome = () => {
    // Reset form and navigate to home
    setStep(1);
    setBookingData({
      tripType: 'one-way',
      vehicleType: '',
      pickupLocation: '',
      dropLocation: '',
      date: '',
      time: '',
      passengers: 1,
      luggage: 0,
      name: '',
      phone: '',
      email: '',
      specialRequests: ''
    });
    setShowSuccess(false);
    navigate('/'); // Navigate to home page
  };

  const renderStep1 = () => (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-2">Trip Type</h3>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setBookingData(prev => ({...prev, tripType: 'one-way'}))}
            className={`p-2 text-xs rounded-lg border ${bookingData.tripType === 'one-way' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'} transition-all`}
          >
            <div className="font-medium">One Way</div>
            <div className="text-gray-500">A → B</div>
          </button>
          <button
            type="button"
            onClick={() => setBookingData(prev => ({...prev, tripType: 'round-trip'}))}
            className={`p-2 text-xs rounded-lg border ${bookingData.tripType === 'round-trip' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'} transition-all`}
          >
            <div className="font-medium">Round Trip</div>
            <div className="text-gray-500">A → B → A</div>
          </button>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <label className="flex items-center text-xs font-medium text-gray-700 mb-1">
            <MapPin size={12} className="mr-1" />
            Pickup Location
          </label>
          <input
            type="text"
            name="pickupLocation"
            value={bookingData.pickupLocation}
            onChange={handleInputChange}
            placeholder="Enter pickup address"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
            required
          />
        </div>

        <div>
          <label className="flex items-center text-xs font-medium text-gray-700 mb-1">
            <MapPin size={12} className="mr-1" />
            Drop Location
          </label>
          <input
            type="text"
            name="dropLocation"
            value={bookingData.dropLocation}
            onChange={handleInputChange}
            placeholder="Enter destination"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="flex items-center text-xs font-medium text-gray-700 mb-1">
            <Calendar size={12} className="mr-1" />
            Date
          </label>
          <input
            type="date"
            name="date"
            value={bookingData.date}
            onChange={handleInputChange}
            min={new Date().toISOString().split('T')[0]}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
            required
          />
        </div>

        <div>
          <label className="flex items-center text-xs font-medium text-gray-700 mb-1">
            <Clock size={12} className="mr-1" />
            Time
          </label>
          <input
            type="time"
            name="time"
            value={bookingData.time}
            onChange={handleInputChange}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="flex items-center text-xs font-medium text-gray-700 mb-1">
            <Users size={12} className="mr-1" />
            Passengers
          </label>
          <select
            name="passengers"
            value={bookingData.passengers}
            onChange={handleInputChange}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
          >
            {[1,2,3,4,5,6,7,8].map(num => (
              <option key={num} value={num}>{num}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="flex items-center text-xs font-medium text-gray-700 mb-1">
            <Luggage size={12} className="mr-1" />
            Luggage
          </label>
          <select
            name="luggage"
            value={bookingData.luggage}
            onChange={handleInputChange}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
          >
            {[0,1,2,3,4].map(num => (
              <option key={num} value={num}>{num} bag{num !== 1 ? 's' : ''}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-800 mb-3">Select Vehicle</h3>
      
      <div className="grid grid-cols-2 gap-3">
        {vehicleTypes.map(vehicle => (
          <button
            key={vehicle.id}
            type="button"
            onClick={() => handleVehicleSelect(vehicle.id)}
            className={`p-3 rounded-lg border text-left transition-all ${bookingData.vehicleType === vehicle.id ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-200' : 'border-gray-200 hover:border-blue-300'}`}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center">
                <div className="bg-white p-1.5 rounded-md shadow-xs mr-2">
                  {vehicle.icon}
                </div>
                <div>
                  <div className="font-semibold text-gray-800 text-xs">{vehicle.name}</div>
                  <div className="text-xs text-gray-600">{vehicle.capacity}</div>
                </div>
              </div>
              {bookingData.vehicleType === vehicle.id && (
                <div className="bg-blue-500 text-white p-0.5 rounded-full">
                  <Check size={10} />
                </div>
              )}
            </div>
            
            <div className="space-y-1">
              {vehicle.features.map((feature, idx) => (
                <div key={idx} className="flex items-center text-xs">
                  <Check size={10} className="text-green-500 mr-1" />
                  <span className="text-gray-700">{feature}</span>
                </div>
              ))}
            </div>
            <div className="mt-2 text-xs font-medium text-blue-600">
              {vehicle.price}
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-800 mb-4">Contact Details</h3>
      
      <div className="grid grid-cols-1 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Full Name *</label>
          <input
            type="text"
            name="name"
            value={bookingData.name}
            onChange={handleInputChange}
            placeholder="Your full name"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
            required
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Phone Number *</label>
          <input
            type="tel"
            name="phone"
            value={bookingData.phone}
            onChange={handleInputChange}
            placeholder="10-digit mobile number"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Email Address</label>
        <input
          type="email"
          name="email"
          value={bookingData.email}
          onChange={handleInputChange}
          placeholder="Your email (optional)"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Special Requests</label>
        <textarea
          name="specialRequests"
          value={bookingData.specialRequests}
          onChange={handleInputChange}
          placeholder="Flight number, child seat, etc."
          rows="2"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200 resize-none"
        />
      </div>

      {/* Booking Summary */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mt-4">
        <h4 className="font-semibold text-gray-800 text-sm mb-3">Booking Summary</h4>
        
        <div className="space-y-2 text-xs">
          <div className="flex justify-between">
            <span className="text-gray-600">Vehicle:</span>
            <span className="font-medium text-gray-800">
              {vehicleTypes.find(v => v.id === bookingData.vehicleType)?.name}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Trip:</span>
            <span className="font-medium text-gray-800">
              {bookingData.pickupLocation} → {bookingData.dropLocation}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Date & Time:</span>
            <span className="font-medium text-gray-800">
              {bookingData.date} | {bookingData.time}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Passengers:</span>
            <span className="font-medium text-gray-800">{bookingData.passengers}</span>
          </div>
        </div>

        <div className="mt-4 p-3 bg-blue-50 rounded border border-blue-100">
          <div className="flex items-center">
            <Shield size={14} className="text-blue-600 mr-2" />
            <div>
              <p className="text-xs text-gray-700">
                Booking secured. You'll get WhatsApp confirmation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderProgressBar = () => (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        {['Trip', 'Vehicle', 'Confirm'].map((label, index) => (
          <div key={index} className="flex flex-col items-center">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${step > index + 1 ? 'bg-green-500 text-white' : step === index + 1 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
              {step > index + 1 ? <Check size={12} /> : index + 1}
            </div>
            <span className={`text-xs mt-1 ${step >= index + 1 ? 'font-medium text-blue-600' : 'text-gray-500'}`}>
              {label}
            </span>
          </div>
        ))}
      </div>
      <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-300"
          style={{ width: `${(step / 3) * 100}%` }}
        ></div>
      </div>
    </div>
  );

  // Success Popup Modal
  const SuccessPopup = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full transform transition-all animate-scale-in">
        <div className="p-6">
          <div className="flex justify-end">
            <button
              onClick={handleReturnHome}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={32} className="text-green-600" />
            </div>
            
            <h3 className="text-xl font-bold text-gray-900 mb-2">Booking Submitted!</h3>
            <p className="text-gray-600 mb-4">
              Your booking request has been sent to Shree Puja Travels via WhatsApp.
            </p>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center space-x-2 text-sm">
                <MessageCircle size={16} className="text-green-600" />
                <span className="font-medium">Check WhatsApp for confirmation</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Our team will contact you shortly to confirm your booking
              </p>
            </div>
            
            <button
              onClick={handleReturnHome}
              className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all flex items-center justify-center"
            >
              <Home size={18} className="mr-2" />
              Return to Home
            </button>
            
            <p className="text-xs text-gray-500 mt-4">
              Booking ID: SPT{Date.now().toString().slice(-8)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen w-full relative">
      {/* Full Screen Background Images */}
      <div className="fixed inset-0 -z-10">
        {/* Mobile Background */}
        <div className="md:hidden absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-fixed"
            style={{
              backgroundImage: 'url("https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80")'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/50 backdrop-blur-sm"></div>
          </div>
        </div>
        
        {/* Desktop Background */}
        <div className="hidden md:block absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-fixed"
            style={{
              backgroundImage: 'url("https://cdn.yatrigo.com/f1le8qeawgm.jpg")'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/40 backdrop-blur-sm"></div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="min-h-screen w-full flex flex-col items-center justify-center py-8 px-4">
        <div className="max-w-md w-full">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-white drop-shadow-lg mb-2">
              Quick Booking
            </h1>
            <p className="text-sm text-gray-100 mb-3 drop-shadow">
              Book in 3 steps • Instant WhatsApp confirmation
            </p>
            <div className="flex items-center justify-center space-x-1">
              <div className="h-0.5 w-8 bg-gradient-to-r from-blue-400 to-blue-500 rounded-full drop-shadow"></div>
              <span className="text-xs font-medium text-white drop-shadow">Shree Puja Travels</span>
              <div className="h-0.5 w-8 bg-gradient-to-r from-blue-500 to-blue-400 rounded-full drop-shadow"></div>
            </div>
          </div>

          {/* Main Booking Card */}
          <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden border border-white/20">
            {/* Progress Bar */}
            <div className="px-6 pt-6">
              {renderProgressBar()}
            </div>

            {/* Form Content */}
            <div className="px-6 pb-6">
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t border-gray-200/50">
                {step > 1 ? (
                  <button
                    type="button"
                    onClick={prevStep}
                    className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-all"
                  >
                    ← Back
                  </button>
                ) : (
                  <div></div>
                )}

                {step < 3 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all flex items-center shadow-lg"
                  >
                    Continue
                    <ArrowRight size={16} className="ml-1" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={sendWhatsAppMessage}
                    className="px-5 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white text-sm font-medium rounded-lg hover:from-green-600 hover:to-green-700 transition-all flex items-center shadow-lg"
                  >
                    <MessageCircle size={16} className="mr-2" />
                    Send via WhatsApp
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="mt-6 grid grid-cols-3 gap-3">
            <div className="bg-white/90 backdrop-blur-sm p-3 rounded-lg border border-white/30 shadow-lg">
              <div className="flex items-center mb-2">
                <div className="bg-blue-100 p-1.5 rounded-full mr-2">
                  <Shield className="text-blue-600" size={14} />
                </div>
                <h3 className="text-xs font-medium text-gray-800">Safe</h3>
              </div>
              <p className="text-xs text-gray-600">Verified drivers & sanitized cars</p>
            </div>

            <div className="bg-white/90 backdrop-blur-sm p-3 rounded-lg border border-white/30 shadow-lg">
              <div className="flex items-center mb-2">
                <div className="bg-green-100 p-1.5 rounded-full mr-2">
                  <Clock className="text-green-600" size={14} />
                </div>
                <h3 className="text-xs font-medium text-gray-800">24/7</h3>
              </div>
              <p className="text-xs text-gray-600">Round-the-clock service</p>
            </div>

            <div className="bg-white/90 backdrop-blur-sm p-3 rounded-lg border border-white/30 shadow-lg">
              <div className="flex items-center mb-2">
                <div className="bg-purple-100 p-1.5 rounded-full mr-2">
                  <Check className="text-purple-600" size={14} />
                </div>
                <h3 className="text-xs font-medium text-gray-800">Instant</h3>
              </div>
              <p className="text-xs text-gray-600">WhatsApp confirmation</p>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 text-center text-white drop-shadow text-xs">
            <p>By booking, you agree to our Terms & Conditions</p>
            {/* <p className="mt-1">© {new Date().getFullYear()} Shree Puja Travels</p> */}
          </div>
        </div>
      </div>

      {/* Success Popup */}
      {showSuccess && <SuccessPopup />}

      {/* Custom Animation */}
      <style jsx global>{`
        html, body {
          margin: 0;
          padding: 0;
          width: 100%;
          height: 100%;
          overflow-x: hidden;
        }
        
        #root {
          width: 100%;
          min-height: 100vh;
        }
        
        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default QuickBooking;